import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { getUnitLabel, getTreatmentLabel } from '../../../../utilties/chartUtilities'
import { isMobile } from '../../../../utilties/mobileDetector'
import { injectIntl } from 'react-intl';
import { compose } from 'recompose'

const BarChart = ({ width, height, data, min, max, intl, canvasHeight }) => {
	const canvas = useRef(null)
	const [ margin, setMargin ] = useState({ top: 20, right: 80, bottom: 30, left: 30 })
	const [ offset, setOffset ] = useState({ top: 20 })

	useEffect(() => {
		if (canvas && canvas.current && data && min != null && max != null) {
			barChart(data, min, max, width, height)
		}

		// Hide tooltip on mobiles when chart scrolled horizontally
		if (document.getElementById('chartWrapper') !== null) {
			document.getElementById('chartWrapper').addEventListener('scroll', ()=>{
				var box = document.getElementById('tooltip');
				box.style.transitionDuration = "0.1s";
				box.style.opacity = "0";
			})
		}
	}, [])

	height = 360 - margin.top - margin.bottom
	if (isMobile) {
		width = window.screen.width -74 - margin.left - margin.right;
	} else {
		width = 616 - margin.left - margin.right;
	}

	const barChart = (data, min, max, width, height) => {

		const svg = d3.select(canvas.current).select('svg').attr('width', width + 100).attr('height', height)
		if (isMobile) {
			let svg = d3.select(canvas.current).select('svg').attr('width', width + 100).attr('height', height).attr('style', 'display: block; margin: 0 auto;')
		}

		// var labels = { myScore: 'Your results', allPatientsCircleScore: 'All results' }
		var labels = { myScore: intl.formatMessage({ id: 'app.chart.yourResultsLabel' }) }

		function drawChart(data) {
			// var subgroups = [ 'myScore', 'allPatientsCircleScore' ]
			var subgroups = [ 'myScore' ]

			var groups = d3
				.map(data, function(d) {
					return d.xAxis
				})
				.keys()

			var x = d3.scaleBand().domain(groups).range([ 0, width ]).padding([ 0.5 ])

			svg.append('g').attr('transform', 'translate(0,' + (height + offset.top) + ')').call(
				d3.axisBottom(x).tickPadding(15).tickSize(0).tickFormat(function(d) {
					let result = data.find(({ xAxis }) => xAxis == d)

					if (result.labelTreatmentPhaseId == 2) return result.labelValue + getUnitLabel(result.labelUnit, intl)
					else return getTreatmentLabel(result.labelTreatmentPhaseId, intl)
				})
			)

			var y = d3.scaleLinear().domain([ min, max ]).range([ height, 0 ])

			svg.append('g').attr('transform', 'translate(0,' + offset.top + ')').call(d3.axisLeft(y))

			var xAxis = d3.axisBottom().scale(x)

			var yAxis = d3.axisRight().scale(y)

			svg
				.append('g')
				.attr('class', 'grid')
				.style('stroke-opacity', '0.6')
				.style('stroke-dasharray', '3, 3')
				.style('color', '#fff')
				.style('shape-rendering', 'crispEdges')
				.attr('transform', 'translate(0, ' + offset.top + ')')
				.call(d3.axisLeft(y).ticks(4).tickSize(-width).tickFormat(''))

			svg
				.append('g')
				.attr('class', 'x axis')
				.attr('color', '#11121C')
				.attr('transform', 'translate(0,255)')
				.append('text')
				.attr('fill', '#11121C')
				.attr('font-size', '16px')
				.attr('transform', 'translate(' + (width - 25) / 2 + ', 70)')
				.text(intl.formatMessage({ id: 'app.chart.time' }))

			svg
				.append('g')
				.attr('class', 'y axis')
				.attr('color', '#11121C')
				.attr('transform', 'translate(' + (width + 0) + ', ' + offset.top + ')')
				.call(
					yAxis.ticks(3).tickPadding(15).tickSize(0).tickFormat(function(d) {
						return d
					})
				)
				.append('text')
				.attr('transform', 'translate(' + width + ', ' + offset.top + ')')
				.attr('transform', 'rotate(-270)')
				.attr('fill', '#11121C')
				.attr('font-size', '16px')
				.attr('font-weight', 'bold')
				.attr('y', -70)
				.attr('x', 170)
				.attr('dy', '.50em')
				.style('text-anchor', 'end')
				.text(intl.formatMessage({ id: 'app.chart.surveyScore' }))

			var xSubgroup = d3.scaleBand().domain(subgroups).range([ 0, x.bandwidth() ]).padding([ 0.05 ])

			var color = d3.scaleOrdinal().domain(subgroups).range([ '#057AFE', '#010E23' ])

			var tooltip = d3
				.select('#tooltip')
				.style('position', 'absolute')
				.style('opacity', 0)
				.style('text-align', 'center')
				.style('width', '172px')
				.style('height', '60px')
				.style('font', '12px sans-serif')
				.style('background', 'white')
				.style('border', '0px')
				.style('pointer-events', 'none')

			svg
				.append('g')
				.selectAll('g')
				.data(data)
				.enter()
				.append('g')
				.attr('transform', function(d) {
					return 'translate(' + x(d.xAxis) + ', ' + offset.top + ')'
				})
				.selectAll('rect')
				.data(function(d) {
					return subgroups.map(function(key) {
						return { key: key, value: d[key] ? d[key] : 'undefined' }
					})
				})
				.enter()
				.append('rect')
				.attr('x', function(d) {
					return xSubgroup(d.key)
				})
				.attr('y', function(d) {
					return y(d.value)
				})
				.attr('width', xSubgroup.bandwidth())
				.attr('height', function(d) {
					return height - y(d.value)
				})
				.attr('fill', function(d) {
					return color(d.key)
				})
				.on('mouseover', function(d) {
					tooltip.transition().duration(200).style('opacity', 1)

					tooltip
						.html(
							"<div style='background-color: #383857" +
								'; border: 1px solid #383857' +
								"; padding: 6px; font-size: 12px; color: white;'>" +
								intl.formatDate(this.parentNode.__data__.completionDate, {year: 'numeric', month: 'short', day: '2-digit'}) +
								'</div>' +
								"<div style='background-color: #383857; font-size: 14px; color: white; padding-top: 8px; padding-bottom: 8px;'>" +
								labels[d.key] +
								' ' +
								d.value +
								'</div>'
						)
						.style('left', d3.event.pageX + 6 + 'px')
						.style('top', d3.event.pageY + 20 + 'px')
						.style('position', 'absolute')
						.style('transform', 'translateX(-50%)')
				})
				.on('mouseout', function(d) {
					tooltip.transition().duration(500).style('opacity', 0)
				})
		}

		drawChart(data)
	}

	const idGenerator = 'chart_' + Math.random().toString(36).substr(2, 9);

	if (isMobile) {
		return (
			<div id='chartWrapper'>
				<div ref={canvas} id={idGenerator} className="patient-bar-chart" data-testid="bar-chart" style={{ width: width + margin.left + margin.right, height: canvasHeight ? canvasHeight : 320, margin: '0 auto' }}>
					<div id='tooltip' />
					<svg />
				</div>
			</div>
		)
	} else {
		return (
			<div ref={canvas} id={idGenerator} className="patient-bar-chart" data-testid="bar-chart" style={{ width: width, height: canvasHeight ? canvasHeight : 320, margin: '0 auto' }}>
				<div id='tooltip' />
				<svg />
			</div>
		)
	}
}


const enhance = compose(injectIntl)

export default enhance(BarChart)
